// SCR-STAFF-882 — اسناد دستی و کارتابل تأیید (US-ACC-021، 023؛ AP-ACC-03): ثبت سند چندردیفی با تفصیلی، ارسال برای تأیید، تأیید یا رد با
// دلیل و سند برگشتی برای اصلاح سند قطعی. حساب کنترلی ماژول‌ها در فرم علامت می‌خورد و بی مجوز `acc.journal.control_override` سرور آن را رد
// می‌کند (PHX-ACC-0803)؛ سند ناتراز هم ذخیره نمی‌شود (PHX-ACC-0802) و همین‌جا پیش از ارسال هشدار داده می‌شود.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type AccJournalListItem,
  type AccJournalStatus,
  type AccManualJournalRequest,
  type AccManualJournalView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  KeyValueList,
  ListPage,
  LoadingState,
  ReasonDialog,
  toBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  accKeys,
  Amount,
  JournalStatusChip,
  num,
  SCR_STAFF_882,
  useAccBranches,
  useBranchName,
} from './common';

/** وضعیت‌های گردش تأیید سند دستی (Posted و Reversed هم برای برگشت سند لازم‌اند). */
const STATUSES: readonly AccJournalStatus[] = ['Draft', 'PendingApproval', 'Rejected', 'Posted'];

interface LineForm {
  accountId: string;
  detail1Id: string;
  detail2Id: string;
  detail3Id: string;
  debit: string;
  credit: string;
  description: string;
}

const EMPTY_LINE: LineForm = {
  accountId: '',
  detail1Id: '',
  detail2Id: '',
  detail3Id: '',
  debit: '',
  credit: '',
  description: '',
};

interface JournalForm {
  branchId: string;
  businessDate: string;
  description: string;
  lines: LineForm[];
}

/** رقم فارسی یا انگلیسی ← ریال صحیح (ADR-013: مبلغ هرگز اعشاری نیست). */
function toRials(value: string): number {
  const digits = value.replace(/[^\d۰-۹٠-٩]/g, '').replace(/[۰-۹٠-٩]/g, (char) => {
    const code = char.charCodeAt(0);
    return String(code >= 0x0660 && code <= 0x0669 ? code - 0x0660 : code - 0x06f0);
  });
  return digits ? Number(digits) : 0;
}

export function ManualJournalsPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const branches = useAccBranches();
  const branchName = useBranchName();
  const full = access.accessMode === 'Full';
  const canCreate = full && access.can('acc.journal.manual.create');
  const canApprove = full && access.can('acc.journal.approve');
  const [status, setStatus] = useState<AccJournalStatus | ''>('');
  const [branchFilter, setBranchFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<AccManualJournalView | 'new' | null>(null);
  const filter = [status, branchFilter].join('|');
  const journals = useInfiniteQuery({
    queryKey: accKeys.manual(session?.tenantId, filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/manual-journals', {
          params: {
            query: {
              status: status || undefined,
              branchId: branchFilter || undefined,
              cursor: pageParam,
              limit: 50,
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const pending = num(journals.data?.pages[0]?.pendingApprovalCount);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['accounting', session?.tenantId] });
  };
  const columns: DataTableColumn<AccJournalListItem>[] = [
    {
      id: 'number',
      headerKey: 'acc:journal.number',
      cell: (row) => (
        <Button size="small" onClick={() => setSelected(row.id)} data-testid="acc-manual-open">
          <bdi dir="ltr">{row.number || t('acc:manual.draft_number')}</bdi>
        </Button>
      ),
    },
    { id: 'date', headerKey: 'acc:common.date', cell: (row) => <JalaliDate value={row.businessDate} /> },
    { id: 'branch', headerKey: 'acc:common.branch', cell: (row) => branchName(row.branchId) },
    { id: 'description', headerKey: 'acc:journal.description', cell: (row) => row.description },
    { id: 'total', headerKey: 'acc:common.total', cell: (row) => <Amount money={row.total} /> },
    { id: 'status', headerKey: 'acc:common.status', cell: (row) => <JournalStatusChip status={row.status} /> },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_882}
      titleKey="acc:manual.title"
      subtitleKey="acc:manual.subtitle"
      badges={
        pending > 0 ? (
          <Chip
            size="small"
            color="warning"
            label={t('acc:manual.pending_count', { count: pending })}
            data-testid="acc-manual-pending"
          />
        ) : undefined
      }
      actions={
        canCreate ? (
          <Button variant="contained" onClick={() => setEditing('new')} data-testid="acc-manual-add">
            {t('acc:manual.add')}
          </Button>
        ) : undefined
      }
      filters={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            select
            size="small"
            label={t('acc:common.status')}
            value={status}
            onChange={(event) => setStatus(event.target.value as AccJournalStatus | '')}
            sx={{ minWidth: 180 }}
            slotProps={{ htmlInput: { 'data-testid': 'acc-manual-status-filter' } }}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`acc:journal_status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('acc:common.branch')}
            value={branchFilter}
            onChange={(event) => setBranchFilter(event.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      }
    >
      <DataTable
        captionKey="acc:manual.title"
        columns={columns}
        rows={journals.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={journals.isPending}
        error={journals.error}
        onRetry={() => void journals.refetch()}
        hasMore={journals.hasNextPage}
        onLoadMore={() => void journals.fetchNextPage()}
        isFetchingMore={journals.isFetchingNextPage}
        emptyTitleKey="acc:manual.empty"
      />
      {selected ? (
        <ManualJournalDetail
          id={selected}
          canApprove={canApprove}
          canEdit={canCreate}
          onEdit={(journal) => {
            setSelected(null);
            setEditing(journal);
          }}
          onChanged={refresh}
          onClose={() => setSelected(null)}
        />
      ) : null}
      {editing ? (
        <ManualJournalForm
          journal={editing === 'new' ? null : editing}
          idempotency={key}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </ListPage>
  );
}

function ManualJournalDetail({
  id,
  canApprove,
  canEdit,
  onEdit,
  onChanged,
  onClose,
}: {
  id: string;
  canApprove: boolean;
  canEdit: boolean;
  onEdit: (journal: AccManualJournalView) => void;
  onChanged: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const branchName = useBranchName();
  const [rejecting, setRejecting] = useState(false);
  const [reversing, setReversing] = useState(false);
  const journal = useQuery({
    queryKey: accKeys.manualJournal(session?.tenantId, id),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/manual-journals/{id}', { params: { path: { id } } }),
      ),
  });
  const after = () => {
    void journal.refetch();
    onChanged();
  };
  const submit = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/accounting/manual-journals/{id}:submit', { params: { path: { id } } })),
    onSuccess: after,
  });
  const approve = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/accounting/manual-journals/{id}:approve', { params: { path: { id } } })),
    onSuccess: after,
  });
  const reject = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/manual-journals/{id}:reject', {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      setRejecting(false);
      after();
    },
  });
  const reverse = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/manual-journals/{id}:reverse', {
          params: { path: { id } },
          body: { reason, businessDate: null },
        }),
      ),
    onSuccess: () => {
      setReversing(false);
      after();
    },
  });
  const data = journal.data;
  const debit = (data?.lines ?? []).reduce((sum, line) => sum + num(line.debit.amount), 0);
  const credit = (data?.lines ?? []).reduce((sum, line) => sum + num(line.credit.amount), 0);
  const control = (data?.lines ?? []).some((line) => line.isControlAccount);
  return (
    <FormDialog
      open
      titleKey="acc:manual.detail_title"
      maxWidth="lg"
      isDirty={false}
      isSubmitting={false}
      submitKey="acc:common.cancel"
      onSubmit={onClose}
      onClose={onClose}
    >
      {journal.isPending ? <LoadingState rows={3} /> : null}
      {journal.error ? <ErrorState error={journal.error} onRetry={() => void journal.refetch()} /> : null}
      {data ? (
        <Stack spacing={2}>
          <KeyValueList
            columns={3}
            items={[
              {
                labelKey: 'acc:journal.number',
                value: (
                  <bdi dir="ltr" data-testid="acc-manual-number">
                    {data.number ?? t('acc:manual.draft_number')}
                  </bdi>
                ),
              },
              { labelKey: 'acc:common.status', value: <JournalStatusChip status={data.status} /> },
              { labelKey: 'acc:common.date', value: <JalaliDate value={data.businessDate} /> },
              { labelKey: 'acc:common.branch', value: branchName(data.branchId) },
              { labelKey: 'acc:journal.kind', value: t(`acc:journal_kind.${data.kind}`) },
              { labelKey: 'acc:common.total', value: <Amount money={data.total} /> },
              { labelKey: 'acc:journal.description', value: data.description },
              {
                labelKey: 'acc:manual.reject_reason',
                value: data.rejectReason ?? '—',
              },
              {
                labelKey: 'acc:manual.approved_at',
                value: data.approvedAt ? <JalaliDate value={data.approvedAt} withTime /> : '—',
              },
            ]}
          />
          {control ? (
            <Alert severity="warning" data-testid="acc-manual-control-warning">
              {t('acc:manual.control_hint')}
            </Alert>
          ) : null}
          {debit !== credit ? (
            <Alert severity="error" data-testid="acc-manual-unbalanced">
              {t('acc:manual.unbalanced')}
            </Alert>
          ) : null}
          <Table size="small" aria-label={t('acc:journal.lines')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('acc:common.level')}</TableCell>
                <TableCell>{t('acc:journal.account')}</TableCell>
                <TableCell>{t('acc:journal.details')}</TableCell>
                <TableCell align="right">{t('acc:journal.debit')}</TableCell>
                <TableCell align="right">{t('acc:journal.credit')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.lines.map((line) => (
                <TableRow key={`${line.lineNo}-${line.accountId}`} data-testid="acc-manual-line">
                  <TableCell>{faDigits(num(line.lineNo))}</TableCell>
                  <TableCell>
                    <bdi dir="ltr">{line.accountCode}</bdi> {line.accountTitle}
                    {line.isControlAccount ? (
                      <Chip
                        size="small"
                        color="warning"
                        sx={{ ms: 1 }}
                        label={t('acc:manual.control_account')}
                        data-testid="acc-manual-control-line"
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {line.details.length === 0
                      ? '—'
                      : line.details.map((detail) => (
                          <div key={detail.id}>
                            <bdi dir="ltr">{detail.code}</bdi> {detail.title}
                          </div>
                        ))}
                  </TableCell>
                  <TableCell align="right">
                    {num(line.debit.amount) > 0 ? <Amount money={line.debit} /> : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {num(line.credit.amount) > 0 ? <Amount money={line.credit} /> : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {submit.error ? <ErrorState error={submit.error} compact /> : null}
          {approve.error ? <ErrorState error={approve.error} compact /> : null}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {canEdit && (data.status === 'Draft' || data.status === 'Rejected') ? (
              <Button size="small" onClick={() => onEdit(data)} data-testid="acc-manual-edit">
                {t('acc:common.edit')}
              </Button>
            ) : null}
            {canEdit && (data.status === 'Draft' || data.status === 'Rejected') ? (
              <Button
                size="small"
                variant="contained"
                disabled={submit.isPending || debit !== credit}
                onClick={() => submit.mutate()}
                data-testid="acc-manual-submit"
              >
                {t('acc:manual.submit')}
              </Button>
            ) : null}
            {canApprove && data.status === 'PendingApproval' ? (
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={approve.isPending}
                onClick={() => approve.mutate()}
                data-testid="acc-manual-approve"
              >
                {t('acc:manual.approve')}
              </Button>
            ) : null}
            {canApprove && data.status === 'PendingApproval' ? (
              <Button
                size="small"
                color="warning"
                onClick={() => setRejecting(true)}
                data-testid="acc-manual-reject"
              >
                {t('acc:manual.reject')}
              </Button>
            ) : null}
            {canApprove && data.status === 'Posted' && data.reversedByJournalEntryId === null ? (
              <Button
                size="small"
                color="warning"
                onClick={() => setReversing(true)}
                data-testid="acc-manual-reverse"
              >
                {t('acc:manual.reverse')}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      ) : null}
      <ReasonDialog
        open={rejecting}
        titleKey="acc:manual.reject"
        messageKey="acc:manual.reject_message"
        confirmKey="acc:manual.reject"
        destructive
        busy={reject.isPending}
        error={reject.error}
        onConfirm={(reason) => reject.mutate(reason)}
        onCancel={() => setRejecting(false)}
      />
      <ReasonDialog
        open={reversing}
        titleKey="acc:manual.reverse"
        messageKey="acc:manual.reverse_message"
        confirmKey="acc:manual.reverse"
        destructive
        busy={reverse.isPending}
        error={reverse.error}
        onConfirm={(reason) => reverse.mutate(reason)}
        onCancel={() => setReversing(false)}
      />
    </FormDialog>
  );
}

function ManualJournalForm({
  journal,
  idempotency,
  onDone,
  onClose,
}: {
  journal: AccManualJournalView | null;
  idempotency: ReturnType<typeof useIdempotencyKey>;
  onDone: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const branches = useAccBranches();
  const accounts = useQuery({
    queryKey: accKeys.accounts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/accounts')),
  });
  const details = useQuery({
    queryKey: accKeys.details(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/detail-accounts')),
  });
  const { control, watch } = useForm<{ businessDate: string | null }>({
    defaultValues: { businessDate: journal?.businessDate ?? toBusinessDate(new Date()) },
  });
  const [form, setForm] = useState<JournalForm>(() => ({
    branchId: journal?.branchId ?? branches[0]?.branchId ?? '',
    businessDate: journal?.businessDate ?? '',
    description: journal?.description ?? '',
    lines:
      journal === null
        ? [{ ...EMPTY_LINE }, { ...EMPTY_LINE }]
        : journal.lines.map((line) => ({
            accountId: line.accountId,
            detail1Id: line.details[0]?.id ?? '',
            detail2Id: line.details[1]?.id ?? '',
            detail3Id: line.details[2]?.id ?? '',
            debit: num(line.debit.amount) > 0 ? String(num(line.debit.amount)) : '',
            credit: num(line.credit.amount) > 0 ? String(num(line.credit.amount)) : '',
            description: line.description ?? '',
          })),
  }));
  const businessDate = watch('businessDate');
  // فقط حساب‌های برگ (بالاترین سطح کدینگ) سند می‌پذیرند؛ حساب میانی ردیف ندارد.
  const leafAccounts = (accounts.data?.items ?? []).filter(
    (account) =>
      account.isActive &&
      !(accounts.data?.items ?? []).some((child) => child.parentId === account.id),
  );
  const detailItems = (details.data?.items ?? []).filter((detail) => detail.isActive);
  const debit = form.lines.reduce((sum, line) => sum + toRials(line.debit), 0);
  const credit = form.lines.reduce((sum, line) => sum + toRials(line.credit), 0);
  const body = (): AccManualJournalRequest => ({
    branchId: form.branchId,
    businessDate: businessDate ?? '',
    description: form.description.trim(),
    templateId: null,
    document: null,
    lines: form.lines
      .filter((line) => line.accountId !== '')
      .map((line) => ({
        accountId: line.accountId,
        detail1Id: line.detail1Id || null,
        detail2Id: line.detail2Id || null,
        detail3Id: line.detail3Id || null,
        debit: toRials(line.debit) > 0 ? { amount: toRials(line.debit), currency: 'IRR' } : null,
        credit: toRials(line.credit) > 0 ? { amount: toRials(line.credit), currency: 'IRR' } : null,
        description: line.description.trim() || null,
      })),
  });
  const save = useMutation({
    mutationFn: () =>
      journal === null
        ? unwrap(
            tenantApi.POST('/api/v1/accounting/manual-journals', {
              body: body(),
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/accounting/manual-journals/{id}', {
              params: { path: { id: journal.id } },
              body: body(),
            }),
          ),
    onSettled: (_data, error) => journal === null && idempotency.settle(error ?? undefined),
    onSuccess: onDone,
  });
  const setLine = (index: number, patch: Partial<LineForm>) =>
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    }));
  return (
    <FormDialog
      open
      titleKey={journal === null ? 'acc:manual.add' : 'acc:manual.edit'}
      maxWidth="lg"
      isDirty={form.description !== '' || form.lines.some((line) => line.accountId !== '')}
      isSubmitting={save.isPending}
      submitError={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          select
          label={t('acc:common.branch')}
          value={form.branchId}
          required
          onChange={(event) => setForm((current) => ({ ...current, branchId: event.target.value }))}
          sx={{ minWidth: 180 }}
          slotProps={{ htmlInput: { 'data-testid': 'acc-manual-branch' } }}
        >
          {branches.map((branch) => (
            <MenuItem key={branch.branchId} value={branch.branchId}>
              {branch.name}
            </MenuItem>
          ))}
        </TextField>
        <JalaliDatePickerRHF control={control} name="businessDate" labelKey="acc:common.date" />
        <TextField
          label={t('acc:journal.description')}
          value={form.description}
          required
          fullWidth
          onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
          slotProps={{ htmlInput: { 'data-testid': 'acc-manual-description' } }}
        />
      </Stack>
      {accounts.isPending || details.isPending ? <LoadingState rows={2} /> : null}
      <Paper variant="outlined" sx={{ p: 1 }}>
        <Table size="small" aria-label={t('acc:journal.lines')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('acc:journal.account')}</TableCell>
              <TableCell>{t('acc:journal.details')}</TableCell>
              <TableCell align="right">{t('acc:journal.debit')}</TableCell>
              <TableCell align="right">{t('acc:journal.credit')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {form.lines.map((line, index) => (
              <TableRow key={index} data-testid="acc-manual-form-line">
                <TableCell sx={{ minWidth: 220 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={line.accountId}
                    onChange={(event) => setLine(index, { accountId: event.target.value })}
                    slotProps={{ htmlInput: { 'data-testid': `acc-manual-account-${index}` } }}
                  >
                    <MenuItem value="">{t('acc:common.none')}</MenuItem>
                    {leafAccounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        <bdi dir="ltr">{account.code}</bdi>&nbsp;{account.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={line.detail1Id}
                    onChange={(event) => setLine(index, { detail1Id: event.target.value })}
                    slotProps={{ htmlInput: { 'data-testid': `acc-manual-detail-${index}` } }}
                  >
                    <MenuItem value="">{t('acc:common.none')}</MenuItem>
                    {detailItems.map((detail) => (
                      <MenuItem key={detail.id} value={detail.id}>
                        <bdi dir="ltr">{detail.code}</bdi>&nbsp;{detail.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    value={line.debit}
                    onChange={(event) => setLine(index, { debit: event.target.value, credit: '' })}
                    slotProps={{
                      htmlInput: {
                        dir: 'ltr',
                        inputMode: 'numeric',
                        'data-testid': `acc-manual-debit-${index}`,
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    value={line.credit}
                    onChange={(event) => setLine(index, { credit: event.target.value, debit: '' })}
                    slotProps={{
                      htmlInput: {
                        dir: 'ltr',
                        inputMode: 'numeric',
                        'data-testid': `acc-manual-credit-${index}`,
                      },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    aria-label={t('acc:manual.remove_line')}
                    disabled={form.lines.length <= 2}
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        lines: current.lines.filter((_, position) => position !== index),
                      }))
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => setForm((current) => ({ ...current, lines: [...current.lines, { ...EMPTY_LINE }] }))}
            data-testid="acc-manual-add-line"
          >
            {t('acc:manual.add_line')}
          </Button>
          <Typography variant="body2" data-testid="acc-manual-total-debit" data-amount={debit}>
            {t('acc:manual.total_debit')}: {faDigits(debit)}
          </Typography>
          <Typography variant="body2" data-testid="acc-manual-total-credit" data-amount={credit}>
            {t('acc:manual.total_credit')}: {faDigits(credit)}
          </Typography>
        </Stack>
      </Paper>
      {debit !== credit ? (
        <Alert severity="warning" data-testid="acc-manual-form-unbalanced">
          {t('acc:manual.unbalanced')}
        </Alert>
      ) : null}
    </FormDialog>
  );
}
